import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";

import type { CityPageInstagramPost } from "@/lib/api";

/** Decorative glyph for the carousel badge; the card's label carries the meaning. */
function StackedPagesIcon(): JSX.Element {
  return (
    <svg aria-hidden viewBox="0 0 16 16" width="12" height="12" fill="none">
      <rect x="1" y="4" width="10" height="10" stroke="currentColor" strokeWidth="1.5" />
      <path d="M5 1h10v10" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

/** True when a card points at one post rather than the city's profile. */
function isPostPermalink(url: string): boolean {
  return /^https:\/\/www\.instagram\.com\/(?:p|reel)\//.test(url);
}

/**
 * One latest post: its first page, and the whole card is a single link to
 * Instagram. The badge and footer live inside that anchor — there is no slider,
 * no nested link and no later-slide request. A cover that is missing, 404s or
 * fails to decode degrades to the same link-only card as a null URL.
 *
 * `priority` marks the row's first card, the one a reader sees before anything
 * else: it loads at full speed, while the rest of the row is deferred and the
 * browser fetches them once the first paint is no longer waiting on a picture.
 * The widths offered are the reduced files the publisher writes beside the full
 * cover; `sizes` is the measured card width at each of the grid's own
 * breakpoints (167 px at 390, 235 px at 800, 177 px at 1024 and 277 px at 1568),
 * rounded up so it can only pick a step larger, never a smaller and blurrier one.
 */
export function InstagramPostCard({
  post,
  cityName,
  priority = false,
}: {
  post: CityPageInstagramPost;
  cityName: string;
  priority?: boolean;
}): JSX.Element {
  const [imageFailed, setImageFailed] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // A new URL deserves a fresh attempt; the flag must not outlive the image it
  // describes (React reuses this component when the card is handed a different
  // post). The settled element is also read here because a prerendered <img> can
  // fail before hydration attaches `onError` — reading `complete`/`naturalWidth`
  // catches exactly that case, so a blocked cover still degrades to the
  // link-only card instead of sitting as a broken frame.
  useEffect(() => {
    const img = imgRef.current;
    setImageFailed(img !== null && img.complete && img.naturalWidth === 0);
  }, [post.imageUrl]);

  const isPost = isPostPermalink(post.instagramUrl);
  const showImage = post.imageUrl !== null && !imageFailed;
  const badge = post.isCarousel
    ? post.pageCount !== null
      ? `1 / ${post.pageCount}`
      : "More pages on Instagram"
    : null;
  const label = !isPost
    ? `Open ${cityName} on Instagram`
    : post.isCarousel && post.pageCount !== null
      ? `Open ${cityName} Instagram post, first of ${post.pageCount} pages`
      : post.isCarousel
        ? `Open ${cityName} Instagram post, more pages on Instagram`
        : `Open ${cityName} Instagram post`;

  return (
    <a
      href={post.instagramUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="atlas-guide-card group block border border-white/15 bg-ink/90"
    >
      <div className="atlas-guide-card-image relative aspect-square w-full">
        {showImage ? (
          <img
            ref={imgRef}
            src={post.imageUrl ?? undefined}
            srcSet={post.imageSrcSet ?? undefined}
            sizes="(max-width: 767px) 48vw, (max-width: 1023px) 32vw, 18vw"
            alt={`${cityName} Instagram post, first page`}
            width={960}
            height={1200}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            onError={() => setImageFailed(true)}
            className="absolute inset-0 h-full w-full object-contain"
          />
        ) : (
          <p className="absolute inset-0 flex items-center justify-center px-4 text-center font-mono text-[0.75rem] font-semibold tracking-wider text-paper-muted">
            Preview unavailable · Open on Instagram
          </p>
        )}
        {badge && (
          <span className="absolute right-2 top-2 flex items-center gap-1 border border-white/25 bg-ink/85 px-2 py-1 font-mono text-[0.75rem] font-semibold tracking-wider text-paper-header">
            <StackedPagesIcon />
            {badge}
          </span>
        )}
      </div>
      <span className="block border-t border-white/15 px-3 py-2 font-mono text-[0.75rem] font-semibold tracking-wider text-paper-header transition-colors group-hover:text-accent group-focus-visible:text-accent">
        {isPost ? "Open on Instagram" : "Open account on Instagram"}
      </span>
    </a>
  );
}
